import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';

const RequestEA = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    eaName: '',
    mql5Url: '',
    platform: 'MT4',
    whatsapp: '',
    notes: ''
  });

  const [status, setStatus] = useState({ loading: false, success: false, error: null });

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus({ loading: true, success: false, error: null });

    try {
      const res = await fetch(`${process.env.REACT_APP_API_URL}/api/forms/request-ea`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });
      if (res.ok) {
        setStatus({ loading: false, success: true, error: null });
        setFormData({ name: '', email: '', eaName: '', mql5Url: '', platform: 'MT4', whatsapp: '', notes: '' });
        setTimeout(() => setStatus({ loading: false, success: false, error: null }), 5000);
      } else {
        setStatus({ loading: false, success: false, error: 'Failed to submit request.' });
      }
    } catch (err) {
      console.error(err);
      setStatus({ loading: false, success: false, error: 'Network error.' });
    }
  };

  return (
    <div className="pt-24 min-h-screen bg-transparent pb-24">
      <div className="max-w-[1200px] mx-auto px-margin-mobile md:px-margin-desktop">
        
        {/* Breadcrumb */}
        <div className="text-[11px] font-bold text-on-surface-variant mb-6 uppercase tracking-widest">
          <Link to="/" className="hover:text-primary transition-colors">Home</Link>
          <span className="mx-2 opacity-50">/</span>
          <span className="text-silver-text">Request EA</span>
        </div>

        <div className="flex flex-col lg:flex-row gap-8">
          
          {/* Left Column: Form */}
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="w-full lg:w-[60%] bg-[#1A1B20] rounded-3xl p-8 md:p-12 border border-white/5"
          >
            {status.success && (
              <div className="mb-8 p-4 bg-green-500/10 border border-green-500/20 rounded-lg text-green-400 font-bold flex items-center gap-3">
                <span className="material-symbols-outlined">check_circle</span>
                Request submitted! We will look into this EA soon.
              </div>
            )}
            {status.error && (
              <div className="mb-8 p-4 bg-red-500/10 border border-red-500/20 rounded-lg text-red-400 font-bold flex items-center gap-3">
                <span className="material-symbols-outlined">error</span>
                {status.error}
              </div>
            )}

            <div className="inline-block bg-[#2A2408] text-primary px-3 py-1 rounded-full text-[10px] font-bold uppercase tracking-[0.2em] mb-6 border border-primary/20">
              SOURCING CHANNEL
            </div>
            
            <h1 className="font-display-md text-headline-lg font-bold text-silver-text mb-4 tracking-tight">
              Can't Find the EA You're Looking For?
            </h1>
            
            <p className="text-on-surface-variant text-[14px] leading-relaxed mb-10 pr-4">
              Our website features only a portion of our collection. If you're looking for a specific MT4 or MT5 Expert Advisor that isn't listed, submit a request and we'll check availability and pricing for you within 24 hours.
            </p>

            <form onSubmit={handleSubmit} className="space-y-6">
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-[12px] font-bold text-silver-text mb-2">
                    Name <span className="text-red-500">*</span>
                  </label>
                  <input type="text" name="name" required value={formData.name} onChange={handleChange} className="w-full bg-[#121316] border border-white/5 rounded-xl px-5 py-4 text-[14px] text-silver-text placeholder-on-surface-variant/50 focus:outline-none focus:border-primary/50 transition-colors" />
                </div>
                <div>
                  <label className="block text-[12px] font-bold text-silver-text mb-2">
                    Email <span className="text-red-500">*</span>
                  </label>
                  <input type="email" name="email" required value={formData.email} onChange={handleChange} className="w-full bg-[#121316] border border-white/5 rounded-xl px-5 py-4 text-[14px] text-silver-text placeholder-on-surface-variant/50 focus:outline-none focus:border-primary/50 transition-colors" />
                </div>
              </div>

              {/* EA Name */}
              <div>
                <label className="block text-[12px] font-bold text-silver-text mb-2">
                  EA Name <span className="text-red-500">*</span>
                </label>
                <input 
                  type="text"
                  name="eaName"
                  required
                  value={formData.eaName}
                  onChange={handleChange}
                  placeholder="e.g. Gold Scalper Pro"
                  className="w-full bg-[#121316] border border-white/5 rounded-xl px-5 py-4 text-[14px] text-silver-text placeholder-on-surface-variant/50 focus:outline-none focus:border-primary/50 transition-colors"
                />
              </div>

              {/* MQL5 URL */}
              <div>
                <label className="block text-[12px] font-bold text-silver-text mb-2">
                  MQL5 Listing URL <span className="text-on-surface-variant font-normal">(optional)</span>
                </label>
                <input 
                  type="url"
                  name="mql5Url"
                  value={formData.mql5Url}
                  onChange={handleChange}
                  placeholder="Paste the MQL5 link if you have it"
                  className="w-full bg-[#121316] border border-white/5 rounded-xl px-5 py-4 text-[14px] text-silver-text placeholder-on-surface-variant/50 focus:outline-none focus:border-primary/50 transition-colors"
                />
              </div>

              {/* Platform Radios */}
              <div>
                <label className="block text-[12px] font-bold text-silver-text mb-3">
                  Platform <span className="text-red-500">*</span>
                </label>
                <div className="flex flex-wrap gap-6">
                  <label className="flex items-center gap-2 cursor-pointer group">
                    <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center ${formData.platform === 'MT4' ? 'border-primary' : 'border-on-surface-variant group-hover:border-white/50'}`}>
                      {formData.platform === 'MT4' && <div className="w-2 h-2 rounded-full bg-primary" />}
                    </div>
                    <span className="text-[13px] text-silver-text">MT4 (MetaTrader 4)</span>
                    <input type="radio" name="platform" value="MT4" checked={formData.platform === 'MT4'} onChange={handleChange} className="hidden" />
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer group">
                    <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center ${formData.platform === 'MT5' ? 'border-primary' : 'border-on-surface-variant group-hover:border-white/50'}`}>
                      {formData.platform === 'MT5' && <div className="w-2 h-2 rounded-full bg-primary" />}
                    </div>
                    <span className="text-[13px] text-silver-text">MT5 (MetaTrader 5)</span>
                    <input type="radio" name="platform" value="MT5" checked={formData.platform === 'MT5'} onChange={handleChange} className="hidden" />
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer group">
                    <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center ${formData.platform === 'Both' ? 'border-primary' : 'border-on-surface-variant group-hover:border-white/50'}`}>
                      {formData.platform === 'Both' && <div className="w-2 h-2 rounded-full bg-primary" />}
                    </div>
                    <span className="text-[13px] text-silver-text">Both Platforms</span>
                    <input type="radio" name="platform" value="Both" checked={formData.platform === 'Both'} onChange={handleChange} className="hidden" />
                  </label>
                </div>
              </div>

              {/* Split Fields */}
              <div className="flex flex-col md:flex-row gap-6">
                <div className="flex-1">
                  <label className="block text-[12px] font-bold text-silver-text mb-2">
                    WhatsApp Number <span className="text-red-500">*</span>
                  </label>
                  <input 
                    type="tel"
                    name="whatsapp"
                    required
                    value={formData.whatsapp}
                    onChange={handleChange}
                    placeholder="e.g. +12345678900"
                    className="w-full bg-[#121316] border border-white/5 rounded-xl px-5 py-4 text-[14px] text-silver-text placeholder-on-surface-variant/50 focus:outline-none focus:border-primary/50 transition-colors"
                  />
                </div>
                <div className="flex-1">
                  <label className="block text-[12px] font-bold text-silver-text mb-2">
                    Your Email <span className="text-red-500">*</span>
                  </label>
                  <input 
                    type="email"
                    name="email"
                    required
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="e.g. trader@example.com"
                    className="w-full bg-[#121316] border border-white/5 rounded-xl px-5 py-4 text-[14px] text-silver-text placeholder-on-surface-variant/50 focus:outline-none focus:border-primary/50 transition-colors"
                  />
                </div>
              </div>

              {/* Additional Notes */}
              <div>
                <label className="block text-[12px] font-bold text-silver-text mb-2">
                  Additional Notes
                </label>
                <textarea 
                  name="notes"
                  value={formData.notes}
                  onChange={handleChange}
                  placeholder="Specify preferred settings, trading pairs, broker constraints, timeframe requirements, or custom configurations..."
                  rows="4"
                  className="w-full bg-[#121316] border border-white/5 rounded-xl px-5 py-4 text-[14px] text-silver-text placeholder-on-surface-variant/50 focus:outline-none focus:border-primary/50 transition-colors resize-none"
                />
              </div>

              {/* Submit Button */}
              <div className="pt-4">
                <button 
                  type="submit"
                  className="w-full bg-primary text-[#1A180B] font-bold py-4 rounded-xl flex items-center justify-center gap-2 hover:bg-primary/90 hover:scale-[1.01] transition-all text-[15px] btn-shimmer"
                >
                  Send Request <span className="material-symbols-outlined text-[18px]">arrow_forward</span>
                </button>
              </div>

            </form>
          </motion.div>

          {/* Right Column: Info Cards */}
          <div className="w-full lg:w-[40%] flex flex-col gap-6">
            
            {/* How Sourcing Works */}
            <motion.div 
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5, delay: 0.1 }}
              className="bg-[#1A1B20] rounded-3xl p-8 border border-white/5"
            >
              <div className="flex items-center gap-4 mb-8">
                <div className="w-12 h-12 rounded-full bg-[#2A2408] border border-primary/20 flex items-center justify-center text-primary">
                  <span className="material-symbols-outlined">search</span>
                </div>
                <div>
                  <h3 className="font-bold text-[16px] text-silver-text">How the Sourcing Works</h3>
                  <div className="text-[10px] font-bold text-on-surface-variant uppercase tracking-widest mt-1">3-STEP PROCESS</div>
                </div>
              </div>

              <div className="space-y-8 relative">
                {/* Timeline Line */}
                <div className="absolute left-[15px] top-2 bottom-6 w-px bg-white/10 z-0" />
                
                {/* Step 1 */}
                <div className="flex gap-6 relative z-10">
                  <div className="w-8 h-8 rounded-full bg-[#2A2408] border border-primary/30 flex items-center justify-center shrink-0 text-primary mt-1">
                    <span className="material-symbols-outlined text-[14px]">send</span>
                    <div className="absolute -top-2 -right-2 bg-surface text-primary text-[8px] font-bold w-4 h-4 rounded-full flex items-center justify-center border border-white/10">01</div>
                  </div>
                  <div>
                    <h4 className="font-bold text-[14px] text-silver-text mb-1">Submit Request</h4>
                    <p className="text-[12px] text-on-surface-variant leading-relaxed">Provide us with the EA Name, or the direct link to the MQL5 marketplace listing.</p>
                  </div>
                </div>

                {/* Step 2 */}
                <div className="flex gap-6 relative z-10">
                  <div className="w-8 h-8 rounded-full bg-[#2A2408] border border-primary/30 flex items-center justify-center shrink-0 text-primary mt-1">
                    <span className="material-symbols-outlined text-[14px]">database</span>
                    <div className="absolute -top-2 -right-2 bg-surface text-primary text-[8px] font-bold w-4 h-4 rounded-full flex items-center justify-center border border-white/10">02</div>
                  </div>
                  <div>
                    <h4 className="font-bold text-[14px] text-silver-text mb-1">We Sift Our Archives</h4>
                    <p className="text-[12px] text-on-surface-variant leading-relaxed">We scan our network database containing unlocked, original file archives of 1,000+ top-tier EAs.</p>
                  </div>
                </div>

                {/* Step 3 */}
                <div className="flex gap-6 relative z-10">
                  <div className="w-8 h-8 rounded-full bg-[#2A2408] border border-primary/30 flex items-center justify-center shrink-0 text-primary mt-1">
                    <span className="material-symbols-outlined text-[14px]">download</span>
                    <div className="absolute -top-2 -right-2 bg-surface text-primary text-[8px] font-bold w-4 h-4 rounded-full flex items-center justify-center border border-white/10">03</div>
                  </div>
                  <div>
                    <h4 className="font-bold text-[14px] text-silver-text mb-1">Get Offer & Link</h4>
                    <p className="text-[12px] text-on-surface-variant leading-relaxed">We verify the version performance, create optimal setfiles, and email you a direct download listing priced at 60-80% off.</p>
                  </div>
                </div>
              </div>
            </motion.div>

            {/* Vault Guarantee */}
            <motion.div 
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
              className="bg-[#1A1B20] rounded-3xl p-8 border border-white/5"
            >
              <div className="flex items-center gap-4 mb-6">
                <div className="w-12 h-12 rounded-full bg-[#0E2822] border border-[#25D366]/20 flex items-center justify-center text-[#25D366]">
                  <span className="material-symbols-outlined">shield</span>
                </div>
                <div>
                  <h3 className="font-bold text-[16px] text-silver-text">Vault Guarantee</h3>
                  <div className="text-[10px] font-bold text-on-surface-variant uppercase tracking-widest mt-1">WHAT YOU GET</div>
                </div>
              </div>

              <div className="h-px w-full bg-gradient-to-r from-white/10 to-transparent mb-6" />

              <ul className="space-y-4">
                <li className="flex items-start gap-4">
                  <div className="w-8 h-8 rounded border border-white/5 bg-[#121316] flex items-center justify-center shrink-0 text-[#25D366]">
                    <span className="material-symbols-outlined text-[16px]">task</span>
                  </div>
                  <div className="flex items-center h-8">
                    <span className="font-bold text-[13px] text-silver-text">100% genuine MQL5 compiled files</span>
                  </div>
                </li>
                <li className="flex items-start gap-4">
                  <div className="w-8 h-8 rounded border border-white/5 bg-[#121316] flex items-center justify-center shrink-0 text-[#25D366]">
                    <span className="material-symbols-outlined text-[16px]">block</span>
                  </div>
                  <div className="flex items-center h-8">
                    <span className="font-bold text-[13px] text-silver-text">No virus, spyware, or keylogger modifications</span>
                  </div>
                </li>
                <li className="flex items-start gap-4">
                  <div className="w-8 h-8 rounded border border-white/5 bg-[#121316] flex items-center justify-center shrink-0 text-[#25D366]">
                    <span className="material-symbols-outlined text-[16px]">build</span>
                  </div>
                  <div className="flex items-center h-8">
                    <span className="font-bold text-[13px] text-silver-text">Setup files and standard presets included</span>
                  </div>
                </li>
                <li className="flex items-start gap-4">
                  <div className="w-8 h-8 rounded border border-white/5 bg-[#121316] flex items-center justify-center shrink-0 text-[#25D366]">
                    <span className="material-symbols-outlined text-[16px]">sync</span>
                  </div>
                  <div className="flex items-center h-8">
                    <span className="font-bold text-[13px] text-silver-text">Lifetime access & free update cycles</span>
                  </div>
                </li>
              </ul>
            </motion.div>

          </div>
        </div>
      </div>
    </div>
  );
};

export default RequestEA;
